// src/pages/src/lib/unfurlQueue.js
const memoryCache = new Map();            // url -> {expiresAt, data}
const pending = new Map();                // key  -> Promise
const QUEUE = [];                         // { key, task, cacheKey, ttlMs, host, attempt }
let RUNNING = 0;
let LAST_START_AT = 0;

const MAX_CONCURRENCY = 2;                // 全体の同時実行上限
const PER_HOST_MAX = 1;                   // ホスト毎の同時実行上限
const START_SPACING_MS = 250;             // 連続開始間隔
const BASE_RETRY_MS = 1200;               // 429 バックオフ基準

const hostRunning = new Map();            // host -> count

export const normalizeUrl = (raw) => {
  try {
    const u = new URL(raw.startsWith("http") ? raw : `https://${raw}`);
    u.hash = "";
    // 余計なトラッキング系を除去
    ["utm_source","utm_medium","utm_campaign","utm_term","utm_content","gclid","fbclid"].forEach(p => u.searchParams.delete(p));
    return u.toString();
  } catch {
    return raw;
  }
};

const now = () => Date.now();

const getCache = (key) => {
  const v = memoryCache.get(key);
  if (v && v.expiresAt > now()) return v.data;
  memoryCache.delete(key);
  try {
    const raw = sessionStorage.getItem(`unf:${key}`);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (obj.expiresAt > now()) {
      memoryCache.set(key, obj);
      return obj.data;
    }
    sessionStorage.removeItem(`unf:${key}`);
  } catch {}
  return null;
};

const setCache = (key, data, ttlMs) => {
  const rec = { data, expiresAt: now() + ttlMs };
  memoryCache.set(key, rec);
  try { sessionStorage.setItem(`unf:${key}`, JSON.stringify(rec)); } catch {}
};

const canStartForHost = (host) => {
  const n = hostRunning.get(host) || 0;
  return n < PER_HOST_MAX;
};

const startItem = async (item) => {
  RUNNING += 1;
  hostRunning.set(item.host, (hostRunning.get(item.host) || 0) + 1);
  LAST_START_AT = now();

  try {
    const p = item.task();
    pending.set(item.key, p);
    const res = await p; // 成功
    pending.delete(item.key);
    if (item.ttlMs && item.cacheKey) setCache(item.cacheKey, res, item.ttlMs);
    return res;
  } catch (err) {
    pending.delete(item.key);
    // 429 は指数バックオフして 1 回だけ再投入
    if (err && err.status === 429 && item.attempt < 2) {
      const delay = BASE_RETRY_MS * Math.pow(2, item.attempt);
      setTimeout(() => {
        QUEUE.unshift({ ...item, attempt: item.attempt + 1 }); // 優先して再実行
        pump();
      }, delay);
      return;
    }
    throw err;
  } finally {
    RUNNING -= 1;
    hostRunning.set(item.host, Math.max(0, (hostRunning.get(item.host) || 1) - 1));
    pump();
  }
};

const pump = async () => {
  if (RUNNING >= MAX_CONCURRENCY) return;
  if (QUEUE.length === 0) return;

  // スペーシング確保
  const elapsed = now() - LAST_START_AT;
  if (elapsed < START_SPACING_MS) {
    setTimeout(pump, START_SPACING_MS - elapsed);
    return;
  }

  // ホスト制限を満たす先頭を探す
  let idx = -1;
  for (let i = 0; i < QUEUE.length; i++) {
    if (canStartForHost(QUEUE[i].host)) { idx = i; break; }
  }
  if (idx === -1) return; // 全てのホストが上限 → 次の解放を待つ

  const [item] = QUEUE.splice(idx, 1);
  startItem(item);
};

export const enqueue = (key, task, { cacheKey, ttlMs = 7 * 24 * 60 * 60 * 1000 } = {}) => {
  // キャッシュ命中なら即返し
  if (cacheKey) {
    const hit = getCache(cacheKey);
    if (hit) return Promise.resolve(hit);
  }

  // 進行中の同一キーがあれば合流
  const inflight = pending.get(key);
  if (inflight) return inflight;

  // キュー投入
  let host = "default";
  try {
    const u = new URL(cacheKey || "");
    host = u.host || "default";
  } catch {}

  const item = { key, task, cacheKey, ttlMs, host, attempt: 0 };
  const promise = new Promise((resolve, reject) => {
    // 実行完了を “pending の Promise” 経由で受け取る
    const run = async () => {
      try {
        const res = await new Promise((res, rej) => {
          // pending.set は startItem 内で行う。ここでは resolve/reject を連結しない
          // かわりに task() の戻り Promise を返す startItem の完了に乗る
          // このラッパ Promise 自体は resolve/reject しないので noop
          res(); // ダミー
        });
        resolve(res);
      } catch (e) {
        reject(e);
      }
    };
    // 実際の解決は pending の Promise で
  });

  QUEUE.push(item);
  pump();

  // 呼び出し側に返すのは “pending map” に入る実体の Promise。
  // まだ start してないので、軽くポーリングして取得（シンプルに）
  return new Promise((resolve, reject) => {
    const spin = () => {
      const p = pending.get(key);
      if (p) {
        p.then(resolve).catch(reject);
      } else if (!QUEUE.find((q) => q.key === key)) {
        // 例外的に queue からも消えている → エラー扱い
        reject(new Error("enqueue failed"));
      } else {
        setTimeout(spin, 10);
      }
    };
    spin();
  });
};
